// Resolve an on-screen swatch color for an apparel color object.
//
// Apparel mills (SanMar, S&S) usually don't send a hex — they send a color
// NAME plus an approximate Pantone code (often the extended 4-digit set, e.g.
// "2389C"). We resolve a swatch by name first (most reliable for apparel, where
// names like "Navy"/"Royal"/"Red" are conventional), then by Pantone code, and
// finally give up (caller keeps its neutral fallback). Values are approximate,
// meant for a swatch preview — not print matching.

// ── Name → hex ───────────────────────────────────────────────────────────────
// Keys are normalized (lowercase, punctuation stripped, spaces collapsed).
const NAME_HEX = {
  // Core
  'white':'#FFFFFF','black':'#171717','jet black':'#0A0A0A','off white':'#F2F0E9',
  'navy':'#1F2A44','deep navy':'#14213D','true navy':'#1F2A44','classic navy':'#1F2A44',
  'red':'#C8102E','true red':'#C8102E','rich red':'#B31B1B','fiery red':'#CE2029',
  'royal':'#1E4CA1','royal blue':'#1E4CA1','true royal':'#1E4CA1',
  'purple':'#6B2D8B','violet':'#7F4FC9','eggplant':'#5E4157',
  'orange':'#FF7F00','safety orange':'#FF6700','s orange':'#FF6700','texas orange':'#BF5700',
  'orange sherbet':'#F4906A',
  'yellow':'#FEDD00','gold':'#D4AF37','lemon yellow':'#FDE910','daffodil yellow':'#FFD300',
  'dafodil yellow':'#FFD300','maize':'#FBEC5D',
  'green':'#2E8B57','forest':'#1F4D2E','forest green':'#1F4D2E','dark green':'#1B4D2E',
  'kelly':'#3AA655','kelly green':'#3AA655','irish green':'#009A44','safety green':'#B0E00E',
  's green':'#B0E00E','dill green':'#6D8C3C','jade green':'#00A86B','stonewash green':'#7D8471',
  'celadon':'#ACE1AF','tropic celadon':'#A8D5BA','tr celadon':'#A8D5BA','olive':'#708238',
  'maroon':'#6E1423','athletic maroon':'#6E1423','ath maroon':'#6E1423','burgundy':'#800020',
  'cardinal':'#8C1D40','sangria':'#92000A',
  'brown':'#5C4033','coyote brown':'#81613C','coyote brn':'#81613C','coyote':'#81613C',
  'dusty brown':'#8B7355','chocolate':'#3F2A1E',
  'tan':'#D2B48C','natural':'#E8DFC8','sand':'#C2B280','light sand':'#D8CBB0','stone':'#C2B7A0',
  'khaki':'#B7A369','biscuit':'#E4D5B7',
  'silver':'#C7C9C7','charcoal':'#3C4348','ash':'#B2BEB5','graphite':'#4B4F54',
  'gray':'#8A8D8F','grey':'#8A8D8F','medium gray':'#8C8C8C','medium grey':'#8C8C8C',
  'dark grey':'#4A4A4A','dark gray':'#4A4A4A','dark heather grey':'#4A4A4A',
  'dark heather gray':'#4A4A4A','drk hthr grey':'#4A4A4A','athletic heather':'#B0B0B0',
  'ath heather':'#B0B0B0','athletic hthr':'#B0B0B0','heather':'#B0B0B0','heather grey':'#9AA0A6',
  'sport grey':'#97999B','sport gray':'#97999B','graphite heather':'#5B5F63',
  // Blues / teals / pinks
  'teal':'#1F7A7A','deep marine':'#1F3A5F','deep teal':'#0F5C5C',
  'light blue':'#A7C7E7','carolina blue':'#4B9CD3','columbia blue':'#8FB8DE',
  'aquatic blue':'#5BC2E7','sky':'#8FD0EF','sky blue':'#8FD0EF','tundra blue':'#6E8CA8',
  'sapphire':'#0F52BA','ultramarine':'#3F5CE6','stonewash blue':'#5D7B93','denim':'#4C6A92',
  'pink':'#FF8DA1','flush pink':'#F26B8A','candy pink':'#E4287C','hot pink':'#E3298C',
  'light pink':'#F6C6D4','lime':'#9BE01E','neon green':'#8FE310','neon yellow':'#EDFF21',
};

// ── Pantone → hex (classic Coated; extended codes are sparse) ─────────────────
const PMS_HEX = {
  '185':'#E4002B','186':'#C8102E','187':'#A6192E','199':'#D50032','200':'#BA0C2F',
  '021':'#FE5000','286':'#0033A0','287':'#003087','288':'#002D72','289':'#002855',
  '072':'#10069F','293':'#003DA5','2935':'#0057B8','300':'#005EB8','2685':'#56008A',
  '348':'#00843D','355':'#009639','356':'#007A33','361':'#43B02A','347':'#009A44',
  '116':'#FFCD00','109':'#FFD200','red032':'#EF3340','processblue':'#0085CA',
  'reflexblue':'#001489','black':'#2D2926','black6':'#101820','white':'#FFFFFF',
  'coolgray7':'#97999B','coolgray9':'#75787B','coolgray11':'#53565A','warmgray8':'#8C8279',
};

function normName(s) {
  if (!s || typeof s !== 'string') return null;
  return s.toLowerCase().replace(/[.\-_/]/g, ' ').replace(/\s+/g, ' ').trim();
}

function normPms(s) {
  if (!s || typeof s !== 'string') return null;
  return s.toUpperCase().replace(/\s*-?\s*(CP|UP|TCX|TPX|TPG|C|U|M)\s*$/, '').replace(/\s+/g, '').toLowerCase();
}

function hexForName(name) {
  const k = normName(name);
  return k ? (NAME_HEX[k] || null) : null;
}

function hexForPms(pms) {
  const k = normPms(pms);
  return k ? (PMS_HEX[k] || null) : null;
}

// Resolve a swatch hex for a color object {hex?, name?, standardName?, pms?}.
// Returns null when nothing matches (caller uses its neutral fallback).
export function resolveColorHex(c) {
  if (!c) return null;
  if (c.hex) return c.hex.startsWith('#') ? c.hex : `#${c.hex}`;
  return hexForName(c.name)
    || hexForName(c.standardName)
    || hexForPms(c.pms)
    || null;
}

export { hexForName, hexForPms };
